import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { Autocomplete, Checkbox, TextField, MenuItem, FormControl, FormHelperText, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { selectCurrentScope } from "../../../app/auth/authSlice";
import { selectCurrentChapterAdmin } from "../../../app/features/chapterAdminSlice";
import { selectCurrentSig } from "../../../app/features/sigSlice";
import { usePaginationEventTypeMutation } from "../../../app/services/eventTypeServices";
import { useLazyGetAllChaptersQuery } from "../../../app/services/chapterServices";
import { useLazyGetAllSIGGroupQuery } from "../../../app/services/SIGGroupServices";
import { useGenerateEventReportMutation } from "../../../app/services/eventService";
import TopHeader from "../../../Component/Header/TopHeader";
import { Container } from 'react-bootstrap';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import { toast } from "react-toastify";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const EventReport = () => {
  const scope = useSelector(selectCurrentScope);
  const chapterDetails = useSelector(selectCurrentChapterAdmin);
  const sigDetails = useSelector(selectCurrentSig);
  // const [getAllEventTypes, { data: eventTypesData }] = useLazyGetAllEventTypeQuery();
  const [getPagitionEventType, { data: eventTypesData }] = usePaginationEventTypeMutation();
  const [getAllChapters, { data: chaptersData }] = useLazyGetAllChaptersQuery();
  const [getAllSIGs, { data: sigsData }] = useLazyGetAllSIGGroupQuery();
  const [generateReport, { isLoading }] = useGenerateEventReportMutation();

  const EVENT_LEVELS = ["International", "National", "State", "Regional", "Local"];
  const DELIVERY_MODES = ["Online", "In-Person", "Hybrid"];
  const OUTPUT_OPTIONS_LIST = [
    "Event Summary",
    "Organisers",
    "Sponsors",
    "Speakers",
    "Participant Statistics",
    "Event Photographs",
    "Links",
  ];

  const { control, register, watch, reset, setValue, clearErrors, handleSubmit } = useForm({
    defaultValues: {
      startDate: "",
      endDate: "",
      eventLevel: [],
      eventType: [],
      modeOfDelivery: [],
      primaryOrganiser: "",   // 0 = Association, 1 = Chapter, 2 = SIG
      chapterId: "",
      sigId: "",
      includeOptions: [],
      exportFormat: 0,        // 0 = Word, 1 = Excel
      sortBy: 0,               // 0 = Event Date, 1 = Organiser, 2 = Event Level
    },
    mode: "onChange",
  });

  useEffect(() => {
    getPagitionEventType({ page: 1, itemsPerPage: '', isDeleted: 0 });
    getAllChapters();
    getAllSIGs();
  }, [getPagitionEventType, getAllChapters, getAllSIGs]);

  useEffect(() => {
    if (scope === "PORTAL_CHAPTER_ADMIN") {
      setValue("primaryOrganiser", 1);
      if (chapterDetails?.chapterName || chapterDetails?.id || chapterDetails?._id) {
        setValue("chapterId", chapterDetails?._id || chapterDetails?.id);
      }
    } else if (scope === "PORTAL_SIG_ADMIN") {
      setValue("primaryOrganiser", 2);
      if (sigDetails?.groupName || sigDetails?.id || sigDetails?._id) {
        setValue("sigId", sigDetails?._id || sigDetails?.id);
      }
    }
  }, [scope, chapterDetails, sigDetails, setValue]);

  const handleFormSubmit = async (formData) => {
    const payload = {
      startDate: formData.startDate,
      endDate: formData.endDate,
      eventLevel: formData.eventLevel,
      eventType: formData.eventType,
      modeOfDelivery: formData.modeOfDelivery,
      primaryOrganiser: formData.primaryOrganiser,
      chapterId: formData.primaryOrganiser === 1 ? formData.chapterId : null,
      sigId: formData.primaryOrganiser === 2 ? formData.sigId : null,
      sortBy: formData.sortBy,
      includeOptions: formData.includeOptions,
      exportFormat: formData.exportFormat,
    };

    try {
      const { blob, contentType } = await generateReport(payload).unwrap();

      if (contentType && contentType.includes('application/json')) {
        const text = await blob.text();
        try {
          const data = JSON.parse(text);
          toast.info(data.message || "No records found for the selected criteria.");
        } catch (e) {
          toast.info("No records found for the selected criteria.");
        }
        return;
      }

      if (blob.size === 0) {
        toast.info("No records found for the selected criteria.");
        return;
      }

      let extension = 'html';
      if (contentType.includes('spreadsheetml') || contentType.includes('excel')) {
        extension = 'xlsx';
      } else if (contentType.includes('wordprocessingml') || contentType.includes('msword')) {
        extension = 'docx';
      } else if (contentType.includes('pdf')) {
        extension = 'pdf';
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Event_Report.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report generated successfully");

      reset({
        startDate: "",
        endDate: "",
        eventLevel: [],
        eventType: [],
        modeOfDelivery: [],
        primaryOrganiser: scope === "PORTAL_CHAPTER_ADMIN" ? 1 : scope === "PORTAL_SIG_ADMIN" ? 2 : "",
        chapterId: scope === "PORTAL_CHAPTER_ADMIN" ? (chapterDetails?._id || chapterDetails?.id) : "",
        sigId: scope === "PORTAL_SIG_ADMIN" ? (sigDetails?._id || sigDetails?.id) : "",
        includeOptions: [],
        exportFormat: 0,
        sortBy: 0,
      });
    } catch (err) {
      console.error("Failed to generate report", err);
      toast.error("Failed to generate report. Please try again.");
    }
  };

  const primaryOrganiser = watch("primaryOrganiser");

  return (
    <div>
      <TopHeader />
      <div className="pt-5">
        <div className="chapter-heading">
          <h2 className="text-center title-font text-light py-3 mt-1">Generate Event Report</h2>
        </div>
      </div>
      <div className='container'>
        <div className='row'>
          <div className='col-md-8'></div>
          <div className='col-md-4 mt-4'>
            <Link to='/'><Button variant="contained" className="menus-color" style={{ float: 'right' }}>Back</Button></Link>
          </div>
        </div>
      </div>
      <Container maxwidth="md" style={{ marginTop: '25px', marginBottom: '25px' }}>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          <div className="row mb-4">
            <div className="col-md-4">
              <Controller
                control={control}
                name="eventLevel"
                rules={{ validate: (value) => (value && value.length > 0) || "Please select at least one Event Level" }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    limitTags={2}
                    id="event-levels-select"
                    className="bg-white"
                    options={EVENT_LEVELS}
                    disableCloseOnSelect
                    value={field.value || []}
                    onChange={(event, newValue) => field.onChange(newValue)}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option}
                      </li>
                    )}
                    style={{ width: '100%' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<Typography className='text-dark'>Event Level<span className='text-danger'>*</span></Typography>}
                        InputLabelProps={{ shrink: true }}
                        placeholder="Select Event Level"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </div>

            <div className="col-md-4">
              <Controller
                control={control}
                name="eventType"
                rules={{ validate: (value) => (value && value.length > 0) || "Please select at least one Event Type" }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    limitTags={2}
                    id="event-types-select"
                    className="bg-white"
                    options={eventTypesData?.data?.eventTypeDetails || []}
                    disableCloseOnSelect
                    value={(eventTypesData?.data?.eventTypeDetails || []).filter(v => (field.value || []).includes(v._id))}
                    onChange={(event, newValue) => field.onChange(newValue?.map(v => v._id) || [])}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option.name}
                      </li>
                    )}
                    style={{ width: '100%' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<Typography className='text-dark'>Event Type<span className='text-danger'>*</span></Typography>}
                        InputLabelProps={{ shrink: true }}
                        placeholder="Select Event Type"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </div>

            <div className="col-md-4">
              <Controller
                control={control}
                name="modeOfDelivery"
                rules={{ validate: (value) => (value && value.length > 0) || "Please select at least one Mode of Delivery" }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    limitTags={2}
                    id="delivery-modes-select"
                    className="bg-white"
                    options={DELIVERY_MODES}
                    disableCloseOnSelect
                    value={field.value || []}
                    onChange={(event, newValue) => field.onChange(newValue)}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option}
                      </li>
                    )}
                    style={{ width: '100%' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<Typography className='text-dark'>Mode of Delivery<span className='text-danger'>*</span></Typography>}
                        InputLabelProps={{ shrink: true }}
                        placeholder="Select Mode of Delivery"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </div>
          </div>
          <div className="row mb-4">
            {scope !== "PORTAL_CHAPTER_ADMIN" && scope !== "PORTAL_SIG_ADMIN" && (
              <div className="col-md-4 mb-4">
                <Controller
                  control={control}
                  name="primaryOrganiser"
                  rules={{
                    validate: (value) => (value === "" || value === undefined || value === null)
                      ? "Please select a Primary Organiser"
                      : true,
                  }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <TextField
                        select
                        label={<Typography className='text-dark'>Primary Organiser<span className='text-danger'>*</span></Typography>}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          setValue("chapterId", "");
                          setValue("sigId", "");
                          clearErrors(["chapterId", "sigId"]);
                        }}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldState.error}
                      >
                        <MenuItem value="">Select Organiser</MenuItem>
                        <MenuItem value={0}>Association</MenuItem>
                        <MenuItem value={1}>Chapter</MenuItem>
                        <MenuItem value={2}>SIG</MenuItem>
                      </TextField>
                      <FormHelperText>{fieldState.error?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </div>
            )}
            {primaryOrganiser === 1 && (
              <div className="col-md-4 mb-4">
                <Controller
                  control={control}
                  name="chapterId"
                  rules={{ required: "Please select a Chapter" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      select
                      label={<Typography className='text-dark'>Select Chapter <span className='text-danger'>*</span></Typography>}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      disabled={scope === "PORTAL_CHAPTER_ADMIN"}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    >
                      <MenuItem value="">Select Chapter</MenuItem>
                      {chaptersData?.data?.map((chapter) => (
                        <MenuItem key={chapter._id || chapter.id} value={chapter._id || chapter.id}>{chapter.title}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </div>
            )}

            {primaryOrganiser === 2 && (
              <div className="col-md-4 mb-4">
                <Controller
                  control={control}
                  name="sigId"
                  rules={{ required: "Please select a SIG" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      select
                      label={<Typography className='text-dark'>Select SIG <span className='text-danger'>*</span></Typography>}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      disabled={scope === "PORTAL_SIG_ADMIN"}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    >
                      <MenuItem value="">Select SIG</MenuItem>
                      {sigsData?.data?.map((sig) => (
                        <MenuItem key={sig._id || sig.id} value={sig._id || sig.id}>{sig.groupName}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </div>
            )}
            <div className="col-md-4">
              <TextField
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                {...register("startDate")}
              />
            </div>
            <div className="col-md-4">
              <TextField
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                {...register("endDate")}
              />
            </div>
            <div className="col-md-4 mb-4">
              <Controller
                control={control}
                name="includeOptions"
                rules={{ validate: (value) => (value && value.length > 0) || "Please select at least one Include option" }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    limitTags={2}
                    id="include-options-select"
                    className="bg-white"
                    options={OUTPUT_OPTIONS_LIST}
                    disableCloseOnSelect
                    value={field.value || []}
                    onChange={(event, newValue) => field.onChange(newValue)}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                        {option}
                      </li>
                    )}
                    style={{ width: '100%' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<Typography className='text-dark'>Include <span className='text-danger'>*</span></Typography>} InputLabelProps={{ shrink: true }}
                        placeholder="Select Include Options"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </div>

            <div className="col-md-3 mb-4">
              <label className="form-label d-block fw-bold text-start">Export Format:</label>
              <div className="form-check form-check-inline">
                <input className="form-check-input" type="radio" value={0} {...register("exportFormat")} checked={watch("exportFormat") === 0 || watch("exportFormat") === '0'} onChange={() => setValue("exportFormat", 0)} />
                <label className="form-check-label">Word</label>
              </div>
              <div className="form-check form-check-inline">
                <input className="form-check-input" type="radio" value={1} checked={watch("exportFormat") === 1 || watch("exportFormat") === '1'} onChange={() => setValue("exportFormat", 1)} />
                <label className="form-check-label">Excel</label>
              </div>
            </div>

            <div className="col-md-5 mb-4">
              <label className="form-label d-block fw-bold text-start">Sort By:</label>
              {["Event Date", "Organiser", "Event Level"].map((sortOption, idx) => (
                <div className="form-check form-check-inline" key={sortOption}>
                  <input className="form-check-input" type="radio" checked={watch("sortBy") === idx} onChange={() => setValue("sortBy", idx)} />
                  <label className="form-check-label">{sortOption}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-4">
            <button type="submit" className="btn btn-primary px-5 py-2 menus-color" disabled={isLoading}>
              {isLoading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </form>
      </Container>
    </div>
  );
};

export default EventReport;